import React from 'react';
import Icon from '@ant-design/icons';
import { Button, Modal, Select, Skeleton } from 'antd';
import cx from 'classnames';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router';

import { ReactComponent as CrossSVG } from '../../../../assets/sightx-icons/svg/x-close.svg';
import { RootState } from '../../../../slices/rootReducer';
import {
  CadenceTypesValues,
  pricePerCadence
} from '../../../auth/proRegister/subscriptionTypes';
import { MAX_ALLOWED_SEATS_FOR_PRO } from './constants';
import { fetchCustomerSubscription } from '../../../../slices/customer/customer.thunk';
import { getAddProSeatsInfo } from '../../../../slices/upgradeAccount/addProSeats/selectors';
import { ICard, selectors as customerSelectors } from '../../../../slices/customer/customer';
import { selectors as authSelectors } from '../../../../slices/auth';
import { IAddProSeatsDTO } from '../../../../slices/upgradeAccount/addProSeats/interfaces';
import { addProSeats, previewAddProSeats } from '../../../../slices/upgradeAccount/addProSeats/thunk';
import { capitalizeFirstLetter } from '../../../common/utils/string';
import { getSvgComponentForCreditCardBrand, toMonthDayYearFormat } from '../../../accounts/account/utils';
import { ReactComponent as ChevronIconSVG } from '../../../../assets/sightx-icons/svg/icon-chevron.svg';
import { formatNumberAsUSDPrice } from '../../../../utils/utils';

import styles from './addSeatsModal.module.scss';

interface AddSeatsModalProps {
  onClose: () => void;
  currentSeats: number;
  renewalDate?: Date;
  cadence?: CadenceTypesValues;
}

interface DispatchProps {
  fetchCustomerSubscription: (customerId: string) => void;
  previewAddProSeats: (data: IAddProSeatsDTO) => void;
  addProSeats: (data: IAddProSeatsDTO) => void;
}

interface AddSeatsModalState {
  seatsToAdd: number;
  isUpdatingSubscription: boolean;
  prorationDate: number;
}

interface StateProps {
  amountDueInCents: number | null;
  totalAmountInCents: number | null;
  loadingPrices: boolean;
  isPreview: boolean | null;
  accountId: string;
  card: ICard | null;
}

type Props = RouteComponentProps & AddSeatsModalProps & StateProps & DispatchProps;

class AddSeatsModalBase extends React.Component<Props, AddSeatsModalState> {
  constructor(props: Props) {
    super(props);
    this.state = {
      seatsToAdd: 1,
      isUpdatingSubscription: false,
      prorationDate: 0
    };
    this.handleChangeSeatsToAdd = this.handleChangeSeatsToAdd.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onPayNowClick = this.onPayNowClick.bind(this);
  }

  componentDidMount() {
    const { accountId } = this.props;
    this.props.fetchCustomerSubscription(accountId);
  }

  private handleChangeSeatsToAdd(value: string) {
    this.setState({ seatsToAdd: +value });
  }

  private onNextClick() {
    const { accountId } = this.props;
    const { seatsToAdd } = this.state;
    const prorationDate = this.getCurrentTimeInSeconds();
    this.setState({ isUpdatingSubscription: true, prorationDate });
    this.props.previewAddProSeats({
      seatsToAdd,
      customerId: accountId,
      prorationDate
    });
  }

  private onPayNowClick() {
    const { accountId } = this.props;
    const { seatsToAdd, prorationDate } = this.state;
    this.props.addProSeats({
      seatsToAdd,
      customerId: accountId,
      prorationDate
    });
  }

  private renderSeatsSelection() {
    const { currentSeats, cadence } = this.props;
    const { seatsToAdd } = this.state;
    const seatsToAddOptions = Array.from(
      { length: MAX_ALLOWED_SEATS_FOR_PRO - currentSeats },
      (_, i) => ({ value: i + 1, label: i + 1 })
    );

    if (!cadence) {
      return null;
    }

    return <>
      <span>
        How many seats would you like to add to your account?
        <Select
          className={styles.select}
          value={`${seatsToAdd}`}
          onChange={this.handleChangeSeatsToAdd}
          options={seatsToAddOptions}
          placeholder="0"
          suffixIcon={<Icon component={ChevronIconSVG} />}
        />
      </span>
      <div className={styles.confirmation}>
        <b>{seatsToAdd} </b>
        additional seat{seatsToAdd > 1 ? 's' : ''} will be added, bringing you up to
        <b> {currentSeats + seatsToAdd} </b> seats in total.
      </div>
    </>;
  }

  private renderUpdateContent() {
    const { seatsToAdd } = this.state;
    const {
      cadence,
      renewalDate,
      amountDueInCents,
      card,
      loadingPrices,
      currentSeats
    } = this.props;
    const amountDue = (amountDueInCents || 0) / 100;
    const pricePerCadenceTypesValue = (cadence && pricePerCadence[cadence]) || 0;

    const totalSeats = currentSeats + seatsToAdd;
    const totalAmount = totalSeats * pricePerCadenceTypesValue;

    const detail = loadingPrices ?
      <Skeleton title={false} active paragraph={{ rows: 1 }} /> : <>
        <span>Prorated subscription ({formatNumberAsUSDPrice(pricePerCadenceTypesValue)} x {seatsToAdd} new user)</span>
        <span>{formatNumberAsUSDPrice(amountDue)}</span>
      </>;
    const total = loadingPrices ?
      <Skeleton title={false} active paragraph={{ rows: 1 }} /> :
      <>
        <b>Today's total</b>
        <b>{formatNumberAsUSDPrice(amountDue)}</b>
      </>;
    return <div className={styles.update}>
      <div>
        <div className={styles.subtitle}>
          Charge summary
        </div>
        You will be charged a prorated fee for added seats based on the days left in your current subscription, which
        renews on {toMonthDayYearFormat(renewalDate)}.
        <div className={styles.summary}>
          <div className={styles.row}>
            {detail}
          </div>
          <div className={styles.row}>
            {total}
          </div>
        </div>
      </div>
      <div>
        <div className={styles.subtitle}>
          Payment method
        </div>
        <div className={styles.summary}>
          {card && <div className={styles.row}>
            <span className={styles.card}>{<Icon
              className={styles.cardIcon}
              component={getSvgComponentForCreditCardBrand(card.brand)}
            />}{capitalizeFirstLetter(card.brand)} ending in {card.lastFourDigits}</span>
          </div>}
        </div>
      </div>
      <div>
        <div className={styles.subtitle}>
          Subscription summary
        </div>
        You will be charged {formatNumberAsUSDPrice(totalAmount)} {cadence ? cadence.toLocaleLowerCase() : ''} to your credit card specified
        above.
        <br />
        <br />
        Your next payment will be <b>{toMonthDayYearFormat(renewalDate)}</b>.
      </div>
    </div>;
  }

  private renderContent() {
    const { isUpdatingSubscription } = this.state;
    if (isUpdatingSubscription) {
      return this.renderUpdateContent();
    }
    return this.renderSeatsSelection();
  }

  private renderFooter() {
    const { loadingPrices, isPreview } = this.props;
    const { isUpdatingSubscription } = this.state;

    return <div className={styles.footer}>
      <Button type="default" onClick={this.props.onClose}>
        Cancel
      </Button>
      <Button
        type="primary"
        disabled={loadingPrices}
        loading={!isPreview && loadingPrices}
        onClick={isUpdatingSubscription ?
          this.onPayNowClick :
          this.onNextClick}
      >
        {isUpdatingSubscription ? 'Pay now' : 'Next'}
      </Button>
    </div>;

  }

  private getCurrentTimeInSeconds(): number {
    return Math.round(Date.now() / 1000);
  }

  render() {
    const { isUpdatingSubscription } = this.state;
    return (
      <Modal
        visible
        onCancel={this.props.onClose}
        className={cx(styles.modal, { [styles.update]: isUpdatingSubscription })}
        footer={null}
        width={isUpdatingSubscription ? 600 : 460}
        closeIcon={<Icon className={styles.closeIcon} component={CrossSVG} />}
      >
        <div className={styles.title}>{isUpdatingSubscription ? 'Update Subscription' : 'Add Seats'}</div>
        {this.renderContent()}
        {this.renderFooter()}
      </Modal>
    );
  }
}

const mapStateToProps = (state: RootState): StateProps => {
  const addProSeatsInfo = getAddProSeatsInfo(state);
  const user = authSelectors.getLoggedInUser(state);
  const card = customerSelectors.getSubscriptionCard(state);
  const accountId = user?.account.id;

  return {
    amountDueInCents: addProSeatsInfo.amountDue,
    totalAmountInCents: addProSeatsInfo.total,
    loadingPrices: addProSeatsInfo.loading,
    isPreview: addProSeatsInfo.isPreview,
    accountId: accountId || '',
    card
  };
};

const dispatchToProps: DispatchProps = {
  fetchCustomerSubscription,
  previewAddProSeats,
  addProSeats
};

export const AddSeatsModal = withRouter(connect(mapStateToProps, dispatchToProps)(AddSeatsModalBase));